import { TICKET_FIELD_KEYS } from "@/const/ticketing.constants"
import { toIsoDate } from "@/helpers/date.helpers"
import { isRecord } from "@/helpers/utils"

import type { TicketFieldSchema } from "@/api/useJira.types"
import type { TicketFields } from "@/api/useWorkflow.types"
import type { TicketingSystem } from "@/domain/ticketing"
import type {
  CreateTicketPayload,
  TicketFormValues
} from "./ticketing.helpers.types"

const CORE_FIELD_KEYS: string[] = Object.values(TICKET_FIELD_KEYS)

export const isCoreTicketField = (field: TicketFieldSchema) =>
  CORE_FIELD_KEYS.includes(field.key)

const toScalar = (value: unknown) => {
  if (typeof value === "string") return value || undefined
  if (typeof value === "number") return `${value}`

  return undefined
}

const toFieldValue = (field: TicketFieldSchema, value: unknown) => {
  if (value === undefined || value === null) return undefined

  switch (field.type) {
    case "number": {
      const number = typeof value === "number" ? value : Number(value)

      return value === "" || Number.isNaN(number) ? undefined : number
    }
    case "date":
      return toIsoDate(value)
    case "multi_option": {
      const values = (Array.isArray(value) ? value : [value])
        .map(toScalar)
        .filter((item): item is string => !!item)

      return values.length ? values : undefined
    }
    default:
      return toScalar(value)
  }
}

export const buildTicketFields = (
  fields: TicketFieldSchema[],
  values: TicketFormValues
): TicketFields => {
  const { project, type, title, description } = values
  const ticketFields: TicketFields = {
    project: { id: project },
    type: { id: type },
    title: title || ""
  }

  if (description) ticketFields.description = description

  fields.forEach((field) => {
    if (isCoreTicketField(field)) return

    const value = toFieldValue(field, values[field.key])

    if (value !== undefined) ticketFields[field.key] = value
  })

  return ticketFields
}

export const toTicketFormValues = (fields: TicketFields): TicketFormValues => {
  const { project, type, title, ...rest } = fields

  return { ...rest, project: project.id, type: type.id, title }
}

export const toCreateTicketData = (payload: CreateTicketPayload) =>
  "jiraIssueFields" in payload
    ? { fields: payload.jiraIssueFields.fields }
    : { provider: payload.provider, fields: payload.fields }

// The create endpoint answers { data: { id, name, url } } for every vendor
export const getCreatedTicket = (response: unknown) => {
  const data = isRecord(response) ? response.data : undefined

  if (!isRecord(data)) return null

  const { id, name, url } = data

  if (typeof id !== "string" || typeof name !== "string") return null

  return { id, name, url: typeof url === "string" ? url : undefined }
}

export const getLinkTicketNeedle = (system: TicketingSystem, issueId: string) =>
  `/${issueId}/${system === "jira" ? "jira" : "tickets"}`

export const buildLinkTicketRequest = (
  system: TicketingSystem,
  issueId: string,
  issueKey: string
) => ({
  needle: getLinkTicketNeedle(system, issueId),
  data: system === "jira" ? { issueKey } : { issueKey, vendor: system }
})
